import React, { useState, useMemo, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import "./UserManagement.scss";
import { UserPlus } from "../../assets/Icons/UserPlus";
import { SortIcon } from "../../assets/Icons/SortIcon";
import { SearchIcon } from "../../assets/Icons/SearchIcon";
import FilterFunnelIcon from "../../assets/Icons/FilterFunnelIcon";
import { ExportIcon } from "../../assets/Icons/ExportIcon";
import { USER_MANAGEMENT } from "../../util/constants";
import { ArrowSort } from "../../assets/Icons/ArrowSort";
import { ChevronDown } from "../../assets/Icons/ChevronDown";
import { CloseIcon } from "../../assets/Icons/CloseIcon";
import { NavDownArrow } from "../../assets/Icons/NavArrowDown";
import NavArrowUp from "../../assets/Icons/NavArrowUp";
import { UserModal } from "./UserModal";
import { LoadingIndicator } from "../CommonUIComponents/LoadingIndicator/LoadingIndicator";
import { fetchUserManagementUsers } from "../../api/endpoints/users.endpoints";
import { UserManageemntService } from "../../api/services";
import { setUserRoles } from "../../store/slices/userSlice";
import { createExcelFile } from "../../util/excelUtil";
import {
  fetchRegions,
  fetchSuperRegionsByRegion,
  fetchRolesByRegion,
  selectRegions,
  selectRegionsLoading,
} from "../../store/slices/lookupSlice";

// Static filter options for status
const STATUS_OPTIONS = ["Active", "Inactive"];

// User Management component for displaying and managing users
export const UserManagement = () => {
  const dispatch = useDispatch();

  // Redux selectors for lookup data
  const regions = useSelector(selectRegions);
  const regionsLoading = useSelector(selectRegionsLoading);
  const superRegionsByRegion = useSelector(
    (state) => state.lookup.superRegionsByRegion,
  );
  const rolesByRegion = useSelector((state) => state.lookup.rolesByRegion);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [expandedAccordions, setExpandedAccordions] = useState({
    region: true,
    superRegion: false,
    role: false,
    status: false,
  });
  const [tempFilters, setTempFilters] = useState({
    region: [],
    superRegion: [],
    role: [],
    status: [],
  });
  const [appliedFilters, setAppliedFilters] = useState({
    region: [],
    superRegion: [],
    role: [],
    status: [],
  });
  const [accordionSearchTerms, setAccordionSearchTerms] = useState({
    region: "",
    superRegion: "",
    role: "",
    status: "",
  });

  // Add/Edit User Modal State
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [userModalMode, setUserModalMode] = useState("add"); // "add" or "edit"
  const [selectedUser, setSelectedUser] = useState(null);

  // Selected region for cascading filters
  const [selectedFilterRegion, setSelectedFilterRegion] = useState(null);

  // Compute filter options from API data
  const filterOptions = useMemo(() => {
    // Get region names from API
    const regionNames = regions
      .map((r) => r.regionName || r.name || "")
      .filter(Boolean);

    // Get super region names based on selected region
    let superRegionNames = [];
    if (selectedFilterRegion) {
      const regionData = regions.find(
        (r) => (r.regionName || r.name) === selectedFilterRegion,
      );
      if (regionData) {
        const regionGuid =
          regionData.regionGuid || regionData.guid || regionData.id;
        const superRegions = superRegionsByRegion[regionGuid] || [];
        superRegionNames = superRegions
          .map((sr) => sr.superRegionName || sr.name || "")
          .filter(Boolean);
      }
    }

    // Get role names based on selected region
    let roleNames = [];
    if (selectedFilterRegion) {
      const regionData = regions.find(
        (r) => (r.regionName || r.name) === selectedFilterRegion,
      );
      if (regionData) {
        const regionGuid =
          regionData.regionGuid || regionData.guid || regionData.id;
        const roles = rolesByRegion[regionGuid] || [];
        roleNames = roles
          .map((r) => r.roleName || r.name || "")
          .filter(Boolean);
      }
    }

    return {
      region: regionNames,
      superRegion: superRegionNames,
      role: roleNames,
      status: STATUS_OPTIONS,
    };
  }, [regions, selectedFilterRegion, superRegionsByRegion, rolesByRegion]);

  // Fetch users on component mount
  const loadUsers = async () => {
    try {
      setIsLoading(true);
      const usersData = await UserManageemntService.fetchAllUsers();
      setUsers(usersData?.data?.usersList || []);
    } catch (error) {
      console.error("Error fetching users:", error);
      setUsers([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  // Fetch regions on component mount
  useEffect(() => {
    dispatch(fetchRegions());
  }, [dispatch]);

  // Fetch super regions and roles when a region is selected in filters
  useEffect(() => {
    if (selectedFilterRegion) {
      const regionData = regions.find(
        (r) => (r.regionName || r.name) === selectedFilterRegion,
      );
      if (regionData) {
        const regionGuid =
          regionData.regionGuid || regionData.guid || regionData.id;
        // Only fetch if not already cached
        if (!superRegionsByRegion[regionGuid]) {
          dispatch(fetchSuperRegionsByRegion(regionGuid));
        }
        if (!rolesByRegion[regionGuid]) {
          dispatch(fetchRolesByRegion(regionGuid));
        }
      }
    }
  }, [
    selectedFilterRegion,
    regions,
    dispatch,
    superRegionsByRegion,
    rolesByRegion,
  ]);

  // Filter users based on search term and applied filters
  const filteredUsers = useMemo(() => {
    let result = users;
    // Apply search filter
    if (searchTerm) {
      const lowerSearch = searchTerm.toLowerCase();
      result = result.filter(
        (user) =>
          (user.emailId?.toLowerCase() || "").includes(lowerSearch) ||
          (user.regionName?.toLowerCase() || "").includes(lowerSearch) ||
          (user.superRegionName?.toLowerCase() || "").includes(lowerSearch) ||
          (user.roleName?.toLowerCase() || "").includes(lowerSearch) ||
          (user.countryNames?.toLowerCase() || "").includes(lowerSearch) ||
          (user.isActive ? "active" : "inactive").includes(lowerSearch),
      );
    }

    // Apply dropdown filters
    if (appliedFilters.region.length > 0) {
      result = result.filter((user) =>
        appliedFilters.region.includes(user.regionName),
      );
    }
    if (appliedFilters.superRegion.length > 0) {
      result = result.filter((user) =>
        appliedFilters.superRegion.includes(user.superRegionName),
      );
    }
    if (appliedFilters.role.length > 0) {
      result = result.filter((user) =>
        appliedFilters.role.includes(user.roleName),
      );
    }
    if (appliedFilters.status.length > 0) {
      const statusFilter = appliedFilters.status.map((s) => s === "Active");
      result = result.filter((user) => statusFilter.includes(user.isActive));
    }

    return result;
  }, [users, searchTerm, appliedFilters]);

  // Sort users based on sort configuration
  const sortedUsers = useMemo(() => {
    if (!sortConfig.key) return filteredUsers;
    return [...filteredUsers].sort((a, b) => {
      if (a[sortConfig.key] < b[sortConfig.key]) {
        return sortConfig.direction === "asc" ? -1 : 1;
      }
      if (a[sortConfig.key] > b[sortConfig.key]) {
        return sortConfig.direction === "asc" ? 1 : -1;
      }
      return 0;
    });
  }, [filteredUsers, sortConfig]);

  // Calculate pagination
  const totalPages = Math.ceil(sortedUsers.length / pageSize);
  const paginatedUsers = sortedUsers.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize,
  );

  // Reset to page 1 if current page is out of bounds after filtering
  useEffect(() => {
    if (currentPage > 1 && currentPage > totalPages) {
      setCurrentPage(1);
    }
  }, [currentPage, totalPages]);

  // Handle column sort
  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  // Handle page change
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // Handle add user click
  const handleAddUser = () => {
    setUserModalMode("add");
    setSelectedUser(null);
    setIsUserModalOpen(true);
  };

  // Handle edit user click
  const handleEditUser = (user) => {
    setUserModalMode("edit");
    setSelectedUser(user);
    setIsUserModalOpen(true);
  };

  // Close user modal
  const handleCloseUserModal = () => {
    setIsUserModalOpen(false);
    setSelectedUser(null);
  };

  // Handle form submit
  const handleUserSubmit = async (payload) => {
    try {
      if (userModalMode === "add") {
        await UserManageemntService.addUser(payload);
      } else {
        // TODO: Implement update user API call
        await UserManageemntService.addUser(payload);
      }
      handleCloseUserModal();
      // Refresh the users list
      await loadUsers();

      // Refresh user roles to update the Header component
      try {
        const userRolesResponse = await UserManageemntService.fetchUserRoles(
          "a53f800f-4c24-4974-ae10-6f7da7efb499",
        );
        if (userRolesResponse?.data?.roles) {
          dispatch(setUserRoles(userRolesResponse.data.roles));
        }
      } catch (rolesError) {
        console.error("Error refreshing user roles:", rolesError);
      }
    } catch (error) {
      // Re-throw the error so UserModal can display the error message
      throw error;
    }
  };

  // Handle more actions click
  const handleMoreActions = (user) => {
    // TODO: Implement more actions functionality
    // console.log("More actions for:", user);
  };

  // Toggle filter panel
  const toggleFilterPanel = () => {
    if (!isFilterPanelOpen) {
      // Opening panel - copy applied filters to temp
      setTempFilters({ ...appliedFilters });
      // Restore selected region from applied filters
      if (appliedFilters.region.length > 0) {
        setSelectedFilterRegion(appliedFilters.region[0]);
      } else {
        setSelectedFilterRegion(null);
      }
    }
    setIsFilterPanelOpen(!isFilterPanelOpen);
  };

  // Toggle accordion
  const toggleAccordion = (accordionKey) => {
    setExpandedAccordions((prev) => ({
      ...prev,
      [accordionKey]: !prev[accordionKey],
    }));
  };

  // Handle checkbox change in filter
  const handleFilterCheckbox = (filterKey, value) => {
    // Handle region selection for cascading filters
    if (filterKey === "region") {
      const currentValues = tempFilters.region;
      const isSelected = currentValues.includes(value);

      if (isSelected) {
        // Deselecting region - clear cascading selections
        setTempFilters((prev) => ({
          ...prev,
          region: currentValues.filter((v) => v !== value),
          superRegion: [],
          role: [],
        }));
        setSelectedFilterRegion(null);
      } else {
        // Selecting region - set as the selected region for cascading
        // For cascading, we only allow one region selection at a time
        setTempFilters((prev) => ({
          ...prev,
          region: [value],
          superRegion: [],
          role: [],
        }));
        setSelectedFilterRegion(value);
      }
      return;
    }

    setTempFilters((prev) => {
      const currentValues = prev[filterKey];
      if (currentValues.includes(value)) {
        return {
          ...prev,
          [filterKey]: currentValues.filter((v) => v !== value),
        };
      } else {
        return {
          ...prev,
          [filterKey]: [...currentValues, value],
        };
      }
    });
  };

  // Handle select all for a filter
  const handleSelectAll = (filterKey) => {
    const allOptions = filterOptions[filterKey];
    const currentValues = tempFilters[filterKey];

    // For region, only allow single selection for cascading
    if (filterKey === "region") {
      if (currentValues.length > 0) {
        setTempFilters((prev) => ({
          ...prev,
          region: [],
          superRegion: [],
          role: [],
        }));
        setSelectedFilterRegion(null);
      } else if (allOptions.length === 1) {
        // Only auto-select if there's exactly one region
        setTempFilters((prev) => ({ ...prev, region: [...allOptions] }));
        setSelectedFilterRegion(allOptions[0]);
      }
      return;
    }

    if (currentValues.length === allOptions.length) {
      setTempFilters((prev) => ({ ...prev, [filterKey]: [] }));
    } else {
      setTempFilters((prev) => ({ ...prev, [filterKey]: [...allOptions] }));
    }
  };

  // Apply filters
  const handleApplyFilters = () => {
    setAppliedFilters({ ...tempFilters });
    setIsFilterPanelOpen(false);
    setCurrentPage(1);
  };

  // Cancel filter changes
  const handleCancelFilters = () => {
    setTempFilters({ ...appliedFilters });
    // Restore selected region from applied filters
    if (appliedFilters.region.length > 0) {
      setSelectedFilterRegion(appliedFilters.region[0]);
    } else {
      setSelectedFilterRegion(null);
    }
    setIsFilterPanelOpen(false);
  };

  // Clear all filters
  const handleClearFilters = () => {
    const emptyFilters = {
      region: [],
      superRegion: [],
      role: [],
      status: [],
    };
    setTempFilters(emptyFilters);
    setSelectedFilterRegion(null);
  };

  // Get total selected count
  const getTotalSelectedCount = () => {
    return Object.values(tempFilters).reduce((acc, arr) => acc + arr.length, 0);
  };

  // Clear selection
  const handleClearSelection = () => {
    setTempFilters({
      region: [],
      superRegion: [],
      role: [],
      status: [],
    });
    setSelectedFilterRegion(null);
  };

  // Export users to Excel
  const handleExportToExcel = async () => {
    try {
      // Use filtered/sorted users for export (respects current filters and search)
      const usersToExport = sortedUsers.map((user, index) => ({
        [USER_MANAGEMENT.COLUMNS.S_NO]: index + 1,
        [USER_MANAGEMENT.COLUMNS.EMAIL]: user.emailId || "",
        [USER_MANAGEMENT.COLUMNS.AREA]: user.regionName || "",
        [USER_MANAGEMENT.COLUMNS.SUPER_REGION]: user.superRegionName || "",
        [USER_MANAGEMENT.COLUMNS.REGION_COUNTRY]: user.countryNames || "",
        [USER_MANAGEMENT.COLUMNS.ROLE]: user.roleName || "",
        [USER_MANAGEMENT.COLUMNS.STATUS]: user.isActive ? "Active" : "Inactive",
      }));

      const exportBlob = await createExcelFile([
        {
          name: "Users",
          data: usersToExport,
        },
      ]);

      if (exportBlob) {
        const timestamp = new Date().toISOString().split("T")[0];
        const link = document.createElement("a");
        link.href = URL.createObjectURL(exportBlob);
        link.download = `user-management-${timestamp}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);
      }
    } catch (error) {
      console.error("Error exporting users to Excel:", error);
    }
  };

  // Get filtered options based on search term
  const getFilteredOptions = (filterKey) => {
    const searchTerm = accordionSearchTerms[filterKey].toLowerCase();
    if (!searchTerm) return filterOptions[filterKey];
    return filterOptions[filterKey].filter((option) =>
      option.toLowerCase().includes(searchTerm),
    );
  };

  // Render status badge
  const renderStatus = (isActive) => {
    const statusText = isActive ? "Active" : "Inactive";
    return (
      <span className={`status-badge ${isActive ? "active" : "inactive"}`}>
        {isActive && <span className="status-dot"></span>}
        {statusText}
      </span>
    );
  };

  // Render countries with +N indicator for additional countries
  const renderCountries = (countryNames) => {
    if (!countryNames) return "---";

    // Split comma-separated countries and trim whitespace
    const countries = countryNames
      .split(",")
      .map((c) => c.trim())
      .filter(Boolean);

    if (countries.length === 0) return "---";

    // Show first two countries
    const displayedCountries = countries.slice(0, 2);
    const remainingCountries = countries.slice(2);
    const remainingCount = remainingCountries.length;

    const handleMouseEnter = (e) => {
      const tooltip = e.currentTarget.querySelector(".countries-tooltip");
      if (tooltip) {
        const rect = e.currentTarget.getBoundingClientRect();
        const tooltipWidth = 300; // max-width from CSS
        const tooltipHeight = Math.min(200, tooltip.scrollHeight); // estimate height
        const windowWidth = window.innerWidth;
        const windowHeight = window.innerHeight;

        // Calculate left position - show on left if not enough space on right
        let left = rect.right + 8;
        if (left + tooltipWidth > windowWidth - 20) {
          left = rect.left - tooltipWidth - 8;
        }

        // Calculate top position - adjust if going out of viewport
        let top = rect.top + rect.height / 2;
        if (top + tooltipHeight / 2 > windowHeight - 20) {
          top = windowHeight - tooltipHeight - 20;
        }
        if (top - tooltipHeight / 2 < 20) {
          top = 20 + tooltipHeight / 2;
        }

        tooltip.style.top = `${top}px`;
        tooltip.style.left = `${left}px`;
        tooltip.style.transform = "translateY(-50%)";
      }
    };

    return (
      <div className="countries-cell">
        <span className="countries-text">{displayedCountries.join(", ")}</span>
        {remainingCount > 0 && (
          <span className="countries-more" onMouseEnter={handleMouseEnter}>
            +{remainingCount}
            <span className="countries-tooltip">
              {remainingCountries.join(", ")}
            </span>
          </span>
        )}
      </div>
    );
  };

  // Render sort icon for column header
  const renderSortIcon = (columnKey) => {
    const isActive = sortConfig.key === columnKey;
    const isAscending = isActive && sortConfig.direction === "asc";
    const isDescending = isActive && sortConfig.direction === "desc";

    return (
      <span
        className={`sort-icon ${isActive ? "active" : ""}`}
        onClick={() => handleSort(columnKey)}
      >
        {isAscending ? (
          <NavArrowUp width={20} height={20} color="#a4a3b1" />
        ) : isDescending ? (
          <NavDownArrow width={20} height={20} color="#a4a3b1" />
        ) : (
          <ArrowSort width={24} height={24} color="#a4a3b1" />
        )}
      </span>
    );
  };

  // Render filter accordion
  const renderFilterAccordion = (filterKey, label) => {
    const isExpanded = expandedAccordions[filterKey];
    const options = getFilteredOptions(filterKey);
    const allOptions = filterOptions[filterKey];
    const isAllSelected =
      allOptions.length > 0 &&
      tempFilters[filterKey].length === allOptions.length;

    // Check if this filter should be disabled (superRegion and role require region selection)
    const isDisabled =
      (filterKey === "superRegion" || filterKey === "role") &&
      !selectedFilterRegion;
    const isLoading =
      (filterKey === "superRegion" || filterKey === "role") &&
      selectedFilterRegion &&
      allOptions.length === 0;

    return (
      <div className={`filter-accordion ${isDisabled ? "disabled" : ""}`}>
        <div
          className="filter-accordion-header"
          onClick={() => !isDisabled && toggleAccordion(filterKey)}
        >
          <span className="filter-accordion-title">
            {label}
            {isDisabled && (
              <span className="filter-hint"> (Select Area first)</span>
            )}
            {isLoading && <span className="filter-hint"> (Loading...)</span>}
          </span>
          <ChevronDown
            width={20}
            height={20}
            color="#dedee2"
            className={`accordion-chevron ${isExpanded ? "expanded" : ""}`}
          />
        </div>
        {isExpanded && !isDisabled && (
          <div className="filter-accordion-content">
            <div className="filter-search-container">
              <SearchIcon width={16} height={16} color="#737387" />
              <input
                type="text"
                placeholder="Search"
                value={accordionSearchTerms[filterKey]}
                onChange={(e) =>
                  setAccordionSearchTerms((prev) => ({
                    ...prev,
                    [filterKey]: e.target.value,
                  }))
                }
                className="filter-search-input"
              />
            </div>
            <div className="filter-options">
              {allOptions.length > 0 && (
                <label className="filter-option">
                  <input
                    type="checkbox"
                    checked={isAllSelected}
                    onChange={() => handleSelectAll(filterKey)}
                    disabled={filterKey === "region"}
                  />
                  <span className="checkbox-custom"></span>
                  <span className="filter-option-label">Select All</span>
                </label>
              )}
              {options.map((option) => (
                <label key={option} className="filter-option">
                  <input
                    type="checkbox"
                    checked={tempFilters[filterKey].includes(option)}
                    onChange={() => handleFilterCheckbox(filterKey, option)}
                  />
                  <span className="checkbox-custom"></span>
                  <span className="filter-option-label">{option}</span>
                </label>
              ))}
              {allOptions.length === 0 && !isLoading && (
                <span className="filter-no-options">No options available</span>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      className={`user-management ${isFilterPanelOpen ? "with-filter-panel" : ""}`}
    >
      {/* Loading Indicator */}
      {isLoading && (
        <LoadingIndicator
          show={true}
          fullscreen={false}
          transparent={true}
          className="user-management-loader"
        />
      )}

      <div className="user-management-main">
        <div className="user-management-header">
          <h1 className="user-management-title">{USER_MANAGEMENT.TITLE}</h1>
          <div className="user-management-actions">
            {(() => {
              const hasActiveFilters = Object.values(appliedFilters).some(
                (v) => Array.isArray(v) && v.length > 0,
              );
              return (
                <button
                  className={`filter-button${hasActiveFilters ? " has-active-filters" : ""}`}
                  onClick={toggleFilterPanel}
                  aria-label="Filter"
                >
                  <FilterFunnelIcon width={20} height={20} color="#A4A3B1" />
                </button>
              );
            })()}
            <button
              className="export-button"
              onClick={handleExportToExcel}
              disabled={sortedUsers.length === 0}
            >
              <ExportIcon width={16} height={16} color="#fff" />
              Export to Excel
            </button>
            <div className="search-container">
              <SearchIcon width={16} height={16} color="#737387" />
              <input
                type="text"
                placeholder={USER_MANAGEMENT.SEARCH_PLACEHOLDER}
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1); // Reset to page 1 when search changes
                }}
                className="search-input"
              />
            </div>
            <button className="add-user-button" onClick={handleAddUser}>
              <UserPlus width={16} height={16} color="#fff" />
              {USER_MANAGEMENT.ADD_USER}
            </button>
          </div>
        </div>

        <div className="user-management-table-container">
          <table className="user-management-table">
            <thead>
              <tr>
                <th>{USER_MANAGEMENT.COLUMNS.S_NO}</th>
                <th>
                  <span className="th-content">
                    {USER_MANAGEMENT.COLUMNS.EMAIL}
                    {renderSortIcon("emailId")}
                  </span>
                </th>
                <th>
                  <span className="th-content">
                    {USER_MANAGEMENT.COLUMNS.AREA}
                    {renderSortIcon("regionName")}
                  </span>
                </th>
                <th>
                  <span className="th-content">
                    {USER_MANAGEMENT.COLUMNS.SUPER_REGION}
                    {renderSortIcon("superRegionName")}
                  </span>
                </th>
                <th>
                  <span className="th-content">
                    {USER_MANAGEMENT.COLUMNS.REGION_COUNTRY}
                    {/* {renderSortIcon("regionCountryName")} */}
                  </span>
                </th>
                <th>
                  <span className="th-content">
                    {USER_MANAGEMENT.COLUMNS.ROLE}
                    {renderSortIcon("roleName")}
                  </span>
                </th>
                <th>
                  <span className="th-content">
                    {USER_MANAGEMENT.COLUMNS.STATUS}
                    {renderSortIcon("isActive")}
                  </span>
                </th>
                <th>{USER_MANAGEMENT.COLUMNS.ACTION}</th>
              </tr>
            </thead>
            <tbody key={`tbody-${searchTerm}-${currentPage}-${paginatedUsers.length}`}>
              {!isLoading &&
              paginatedUsers.length === 0 &&
              (appliedFilters.region.length > 0 ||
                appliedFilters.superRegion.length > 0 ||
                appliedFilters.role.length > 0 ||
                appliedFilters.status.length > 0 ||
                searchTerm) ? (
                <tr>
                  <td colSpan="8" className="no-results-row">
                    No results available. Modify the applied filters to view
                    users.
                  </td>
                </tr>
              ) : (
                paginatedUsers.map((user, index) => (
                  <tr key={`${user.userGuId || user.userId || index}-${index}`}>
                    <td>{(currentPage - 1) * pageSize + index + 1}</td>
                    <td>{user.emailId}</td>
                    <td>{user.regionName}</td>
                    <td>{user.superRegionName}</td>
                    <td>{renderCountries(user.countryNames)}</td>
                    <td>{user.roleName}</td>
                    <td>{renderStatus(user.isActive)}</td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="edit-button"
                          onClick={() => handleEditUser(user)}
                        >
                          {USER_MANAGEMENT.EDIT}
                        </button>
                        {/* <button
                        className="more-button"
                        onClick={() => handleMoreActions(user)}
                      >
                        ···
                      </button> */}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="user-management-pagination">
          <div className="pagination-section">
            <div className="page-size-selector">
              <span>{USER_MANAGEMENT.SHOW}</span>
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(1);
                }}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
            </div>
            <div className="pagination-controls">
              <button
                className="pagination-button"
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
              >
                «
              </button>
              <button
                className="pagination-button"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                ‹
              </button>
              <span className="page-info">
                {currentPage} / {totalPages || 1}
              </span>
              <button
                className="pagination-button"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                ›
              </button>
              <button
                className="pagination-button"
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
              >
                »
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Filter Panel */}
      {isFilterPanelOpen && (
        <div className="filter-panel">
          <div className="filter-panel-header">
            <h2 className="filter-panel-title">Filters</h2>
            <button
              className="filter-clear-button"
              onClick={handleClearFilters}
            >
              Clear
            </button>
          </div>
          <p className="filter-panel-subtitle">Select filters below</p>

          <div className="filter-accordions">
            {renderFilterAccordion("region", "Area")}
            {renderFilterAccordion("superRegion", "Super Region")}
            {renderFilterAccordion("role", "Role")}
            {renderFilterAccordion("status", "Status")}
          </div>

          <div className="filter-panel-footer">
            <div className="filter-selected-info">
              <button
                className="clear-selection-button"
                onClick={handleClearSelection}
              >
                <CloseIcon width={14} height={14} color="#dedee2" />
              </button>
              <span>Selected {getTotalSelectedCount()} items</span>
            </div>
            <div className="filter-panel-actions">
              <button
                className="filter-cancel-button"
                onClick={handleCancelFilters}
              >
                Cancel
              </button>
              <button
                className="filter-apply-button"
                onClick={handleApplyFilters}
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit User Modal */}
      <UserModal
        isOpen={isUserModalOpen}
        mode={userModalMode}
        userData={selectedUser}
        existingUsers={users}
        onClose={handleCloseUserModal}
        onSubmit={handleUserSubmit}
      />
    </div>
  );
};

export default UserManagement;
